import React, { useState, useEffect, useRef } from 'react';
import QueueService from './QueueService';
import './BrandSettingsPage.css';

const BrandSettingsPage = ({ token, business_id }) => {
    const [formData, setFormData] = useState({
        id: 0,
        name: "",
        status: "",
        whatsapp_phone_number: "",
        whatsapp_phone_number_id: "",
        whatsapp_business_account_id: "",
        brand_metadata: {
            reservation_flow_id: "",
            reservation_flow_token: "",
            address_flow_id: "",
            product_retailer_id: "",
            offer_message: "",
            catalog_message_text: "",
            tags: [],
            templates: {
                reservation_received_alert: "",
                reservation_confirmed: "",
                queue_confirmed: "",
                turn_ready: "",
                removed_from_queue: "",
                daily_report: "",
                reservation_priority: "",
                feedback_request: "",
                negative_feedback_alert: ""
            }
        }
    });

    const [status, setStatus] = useState({ type: '', message: '' });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const templateKeys = [
        "reservation_received_alert",
        "reservation_confirmed",
        "queue_confirmed",
        "turn_ready",
        "removed_from_queue",
        "daily_report",
        "reservation_priority",
        "feedback_request",
        "negative_feedback_alert"
    ];

    const lastFetchedIdRef = useRef(null);

    useEffect(() => {
        const fetchBrandInfo = async () => {
            if (!business_id) return;
            // Prevent double fetch in StrictMode or rapid re-renders
            if (lastFetchedIdRef.current === business_id) return;
            lastFetchedIdRef.current = business_id;

            try {
                const response = await QueueService.GetBusinessInfo(business_id);
                // response is data object from api response
                // structure based on user input:
                // {
                //   ...
                //   brand: {
                //     id: 2,
                //     name: "Paul",
                //     status: "active",
                //     whatsapp_phone_number: "...",
                //     ...
                //     brand_metadata: { ... }
                //   }
                // }

                const brandData = response.brand;
                if (brandData) {
                    setFormData({
                        id: brandData.id,
                        name: brandData.name || "",
                        status: brandData.status || "active",
                        whatsapp_phone_number: brandData.whatsapp_phone_number || "",
                        whatsapp_phone_number_id: brandData.whatsapp_phone_number_id || "",
                        whatsapp_business_account_id: brandData.whatsapp_business_account_id || "",
                        brand_metadata: {
                            reservation_flow_id: brandData.brand_metadata?.reservation_flow_id || "",
                            reservation_flow_token: brandData.brand_metadata?.reservation_flow_token || "",
                            address_flow_id: brandData.brand_metadata?.address_flow_id || "",
                            product_retailer_id: brandData.brand_metadata?.product_retailer_id || "",
                            offer_message: brandData.brand_metadata?.offer_message || "",
                            catalog_message_text: brandData.brand_metadata?.catalog_message_text || "",
                            tags: brandData.brand_metadata?.tags || [],
                            templates: {
                                ...formData.brand_metadata.templates, // keep defaults/keys
                                ...(brandData.brand_metadata?.templates || {})
                            }
                        }
                    });
                }
            } catch (error) {
                console.error('Error fetching brand info:', error);

                // Fallback for demo/development if API fails or returns no brand data
                // This preserves the initial state structure if request fails
            } finally {
                setLoading(false);
            }
        };

        fetchBrandInfo();
    }, [business_id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'id' ? parseInt(value) || 0 : value
        }));
    };

    const handleMetadataChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            brand_metadata: {
                ...prev.brand_metadata,
                [name]: value
            }
        }));
    };

    const handleTagsChange = (e) => {
        const { value } = e.target;
        // Split string by commas to create array
        const tagsArray = value.split(',').map(tag => tag.trim());
        setFormData(prev => ({
            ...prev,
            brand_metadata: {
                ...prev.brand_metadata,
                tags: tagsArray
            }
        }));
    };

    const handleTemplateChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            brand_metadata: {
                ...prev.brand_metadata,
                templates: {
                    ...prev.brand_metadata.templates,
                    [name]: value
                }
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setStatus({ type: '', message: '' });

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}/business/brand_create_or_update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Referer': apiUrl,
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                throw new Error(`Error: ${response.statusText}`);
            }

            const data = await response.json();
            setStatus({ type: 'success', message: 'Brand settings updated successfully!' });
            console.log('Success:', data);
        } catch (error) {
            console.error('Error:', error);
            setStatus({ type: 'error', message: 'Failed to update brand settings. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="bs-container">Loading...</div>;
    }

    return (
        <div className="bs-container">
            <div className="bs-header">
                <h2>Brand Settings</h2>
            </div>

            <form onSubmit={handleSubmit} className="bs-branding-form">

                {/* Basic Info Section */}
                <div className="bs-section">
                    <h3>Basic Information</h3>
                    <div className="bs-grid">
                        {/* Hidden ID field as requested */}
                        <div className="bs-group">
                            <label htmlFor="name">Brand Name</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Enter brand name"
                            />
                        </div>
                        <div className="bs-group">
                            <label htmlFor="status">Status</label>
                            <select
                                id="status"
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                        <div className="bs-group">
                            <label htmlFor="tags">Tags (comma separated)</label>
                            <input
                                type="text"
                                id="tags"
                                name="tags"
                                value={formData.brand_metadata.tags.join(', ')}
                                onChange={handleTagsChange}
                                placeholder="e.g. tag1, tag2"
                            />
                        </div>
                    </div>
                </div>

                {/* WhatsApp Configuration Section */}
                <div className="bs-section">
                    <h3>WhatsApp Configuration</h3>
                    <div className="bs-grid">
                        <div className="bs-group">
                            <label htmlFor="whatsapp_phone_number">WhatsApp Phone Number</label>
                            <input
                                type="text"
                                id="whatsapp_phone_number"
                                name="whatsapp_phone_number"
                                value={formData.whatsapp_phone_number}
                                onChange={handleInputChange}
                                placeholder="e.g. 919220691966"
                            />
                        </div>
                        <div className="bs-group">
                            <label htmlFor="whatsapp_phone_number_id">WhatsApp Phone Number ID</label>
                            <input
                                type="text"
                                id="whatsapp_phone_number_id"
                                name="whatsapp_phone_number_id"
                                value={formData.whatsapp_phone_number_id}
                                onChange={handleInputChange}
                                placeholder="WhatsApp Phone Number ID"
                            />
                        </div>
                        <div className="bs-group">
                            <label htmlFor="whatsapp_business_account_id">WhatsApp Business Account ID</label>
                            <input
                                type="text"
                                id="whatsapp_business_account_id"
                                name="whatsapp_business_account_id"
                                value={formData.whatsapp_business_account_id}
                                onChange={handleInputChange}
                                placeholder="WhatsApp Business Account ID"
                            />
                        </div>
                    </div>
                </div>

                {/* Meta Configuration Section */}
                <div className="bs-section">
                    <h3>Meta configuration</h3>
                    <div className="bs-grid">
                        <div className="bs-group">
                            <label htmlFor="reservation_flow_id">Reservation Flow ID</label>
                            <input
                                type="text"
                                id="reservation_flow_id"
                                name="reservation_flow_id"
                                value={formData.brand_metadata.reservation_flow_id}
                                onChange={handleMetadataChange}
                                placeholder="Reservation Flow ID"
                            />
                        </div>
                        <div className="bs-group">
                            <label htmlFor="reservation_flow_token">Reservation Flow Token</label>
                            <input
                                type="text"
                                id="reservation_flow_token"
                                name="reservation_flow_token"
                                value={formData.brand_metadata.reservation_flow_token}
                                onChange={handleMetadataChange}
                                placeholder="Reservation Flow Token"
                            />
                        </div>
                        <div className="bs-group">
                            <label htmlFor="address_flow_id">Address Flow ID</label>
                            <input
                                type="text"
                                id="address_flow_id"
                                name="address_flow_id"
                                value={formData.brand_metadata.address_flow_id}
                                onChange={handleMetadataChange}
                                placeholder="Address Flow ID"
                            />
                        </div>
                        <div className="bs-group">
                            <label htmlFor="product_retailer_id">Product Retailer ID</label>
                            <input
                                type="text"
                                id="product_retailer_id"
                                name="product_retailer_id"
                                value={formData.brand_metadata.product_retailer_id}
                                onChange={handleMetadataChange}
                                placeholder="Product Retailer ID"
                            />
                        </div>
                    </div>
                </div>

                {/* Retail & Messaging Section */}
                <div className="bs-section">
                    <h3>Retail & Messaging</h3>
                    <div className="bs-grid">
                        <div className="bs-group">
                            <label htmlFor="catalog_message_text">Catalog Message Text</label>
                            <input
                                type="text"
                                id="catalog_message_text"
                                name="catalog_message_text"
                                value={formData.brand_metadata.catalog_message_text}
                                onChange={handleMetadataChange}
                                placeholder="Text for catalog message"
                            />
                        </div>
                        <div className="bs-group bs-full-width">
                            <label htmlFor="offer_message">Offer Message</label>
                            <textarea
                                id="offer_message"
                                name="offer_message"
                                value={formData.brand_metadata.offer_message}
                                onChange={handleMetadataChange}
                                placeholder="Enter offer message details..."
                                rows="4"
                                className="bs-textarea"
                            />
                        </div>
                    </div>
                </div>

                {/* Templates Section */}
                <div className="bs-section">
                    <h3>Templates</h3>
                    <div className="bs-grid">
                        {templateKeys.map(key => (
                            <div className="bs-group" key={key}>
                                <label htmlFor={key}>{key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</label>
                                <input
                                    type="text"
                                    id={key}
                                    name={key}
                                    value={formData.brand_metadata.templates[key] || ''}
                                    onChange={handleTemplateChange}
                                    placeholder={`Template for ${key.replace(/_/g, ' ')}`}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {status.message && (
                    <div className={`bs-status-message ${status.type === 'success' ? 'success' : 'error'}`}>
                        {status.message}
                    </div>
                )}

                <div className="bs-actions">
                    <button type="button" className="bs-btn bs-btn-secondary" onClick={() => window.history.back()}>
                        Cancel
                    </button>
                    <button type="submit" className="bs-btn bs-btn-primary" disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>

            </form>
        </div>
    );
};

export default BrandSettingsPage;
